/**
 * The file-explorer selection model, mirroring desktop file managers.
 *
 * Plain click replaces the selection · Ctrl/Cmd toggles one · Shift selects the
 * contiguous range from the anchor. Three pieces of state make that work and
 * they only make sense together:
 *
 * - `selectedIds` — what is selected
 * - `anchorId`    — the pivot a Shift-range extends FROM
 * - `focusId`     — where the keyboard is, which is not the same thing
 *
 * The distinction between selected and focused is easy to lose: the UI draws
 * both with a primary ring (selection adds an offset), so a bug that conflates
 * them looks almost right.
 *
 * Selection is capped, because every bulk action operates on it — capping here
 * caps delete, move and copy in one place.
 */

import { useCallback, useEffect, useState } from 'react';

export const MAX_SELECTION = 100;

/**
 * @param items        the ordered rows currently listed — Shift-ranges are
 *                     computed against this order, so it must match what is on
 *                     screen, not the unsorted response
 * @param resetKey     changing this clears the selection; pass whatever
 *                     identifies the current dataset (location + folder)
 * @param onCapped     called when a selection would exceed the cap
 * @param containerRef the scrollable list element. Selecting with the mouse
 *                     returns keyboard focus to it, so arrow keys keep working
 *                     after a click — without this, click-then-arrow is dead.
 */
export function useNodeSelection({ items = [], resetKey, onCapped, containerRef } = {}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [anchorId, setAnchorId] = useState(null);
  const [focusId, setFocusId] = useState(null);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAnchorId(null);
    setFocusId(null);
  }, []);

  // Switching location or folder is a move to a DIFFERENT dataset, so a
  // selection carried over from the previous one is meaningless — and acting
  // on it in bulk would touch rows the user can no longer see.
  useEffect(() => {
    clearSelection();
  }, [resetKey, clearSelection]);

  const orderedIndex = useCallback(
    (id) => items.findIndex((item) => item.id === id),
    [items],
  );

  const toggleSelect = useCallback(
    (id) => {
      // The cap is checked against current state rather than inside the
      // updater: an updater runs later, so a flag set in there is still false
      // when the caller reads it and the warning never fires.
      if (!selectedIds.has(id) && selectedIds.size >= MAX_SELECTION) {
        onCapped?.();
        return;
      }
      setSelectedIds((previous) => {
        const next = new Set(previous);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      setAnchorId(id);
      setFocusId(id);
    },
    [selectedIds, onCapped],
  );

  /** Select anchor→target inclusive. `additive` keeps what was already there. */
  const applyRange = useCallback(
    (fromId, toId, additive) => {
      const from = orderedIndex(fromId);
      const to = orderedIndex(toId);
      if (from < 0 || to < 0) return;
      const [low, high] = from <= to ? [from, to] : [to, from];

      const next = additive ? new Set(selectedIds) : new Set();
      let capped = false;
      for (const item of items.slice(low, high + 1)) {
        if (next.size >= MAX_SELECTION) {
          capped = true;
          break;
        }
        next.add(item.id);
      }
      setSelectedIds(next);
      if (capped) onCapped?.();
    },
    [items, orderedIndex, selectedIds, onCapped],
  );

  /** The click handler a row binds to. */
  const selectRow = useCallback(
    (event, item) => {
      const id = item.id;
      const additive = event.metaKey || event.ctrlKey;

      if (event.shiftKey && anchorId != null) {
        applyRange(anchorId, id, additive);
        setFocusId(id);
      } else if (additive) {
        toggleSelect(id);
      } else {
        setSelectedIds(new Set([id]));
        setAnchorId(id);
        setFocusId(id);
      }

      // Keep keystrokes flowing to the list after a click. Clicking a row
      // moves DOM focus to the row itself, so without this the container's
      // keydown handler stops firing and arrow-key navigation is dead.
      containerRef?.current?.focus({ preventScroll: true });
    },
    [anchorId, applyRange, toggleSelect, containerRef],
  );

  const toggleSelectAll = useCallback(
    (rows) => {
      const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
      if (allSelected) {
        clearSelection();
        return;
      }
      const next = new Set();
      let capped = false;
      for (const row of rows) {
        if (next.size >= MAX_SELECTION) {
          capped = true;
          break;
        }
        next.add(row.id);
      }
      setSelectedIds(next);
      if (capped) onCapped?.();
    },
    [selectedIds, clearSelection, onCapped],
  );

  return {
    selectedIds,
    setSelectedIds,
    anchorId,
    setAnchorId,
    focusId,
    setFocusId,
    selectionCount: selectedIds.size,
    isSelected: (id) => selectedIds.has(id),
    selectRow,
    toggleSelect,
    toggleSelectAll,
    applyRange,
    clearSelection,
    orderedIndex,
  };
}
